export interface BackgroundAudioOptions {
  loop?: boolean
  volume?: number
  fadeIn?: number
}

export interface VoiceAudioOptions {
  volume?: number
}

export type PhoneKeyTickKind = 'character' | 'space' | 'layout'

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value))
const PHONE_KEY_TICK_VARIANTS = 4
const PHONE_KEY_TICK_DURATION = 0.024
const PHONE_KEY_TICK_MIN_GAP = 0.09
const PHONE_KEY_TICK_VOLUME = 0.42 * 0.55
const WOOSH_VOLUME = 0.28

export class AudioController {
  private context: AudioContext | null = null
  private gain: GainNode | null = null
  private source: AudioBufferSourceNode | null = null
  private loadVersion = 0
  private voiceGain: GainNode | null = null
  private voiceSource: AudioBufferSourceNode | null = null
  private voiceLoadVersion = 0
  private phoneKeyTickData: ArrayBuffer | null = null
  private phoneKeyTickBuffer: AudioBuffer | null = null
  private phoneKeyTickLoadVersion = 0
  private phoneKeyTickIndex = 0
  private phoneKeyTickLastAt = Number.NEGATIVE_INFINITY
  private wooshData: ArrayBuffer | null = null
  private wooshBuffer: AudioBuffer | null = null
  private wooshLoadVersion = 0
  private readonly activeWooshSources = new Map<AudioBufferSourceNode, GainNode>()

  public async unlock(): Promise<void> {
    if (!this.context) {
      this.context = new AudioContext({ latencyHint: 'interactive' })
      this.gain = this.context.createGain()
      this.gain.gain.value = 1
      this.gain.connect(this.context.destination)
      this.voiceGain = this.context.createGain()
      this.voiceGain.gain.value = 1
      this.voiceGain.connect(this.context.destination)
    }
    if (this.context.state === 'suspended') await this.context.resume()
    await Promise.all([
      this.decodePreparedPhoneKeyTicks(),
      this.decodePreparedWoosh(),
    ])

    // A one-sample silent source makes the user-gesture unlock explicit on
    // browsers that otherwise defer the first audible Web Audio source.
    const silentBuffer = this.context.createBuffer(1, 1, this.context.sampleRate)
    const silentSource = this.context.createBufferSource()
    silentSource.buffer = silentBuffer
    silentSource.connect(this.gain ?? this.context.destination)
    silentSource.start()
  }

  public async playBackground(
    url: string,
    options: BackgroundAudioOptions = {},
  ): Promise<AudioBufferSourceNode> {
    const cleanUrl = url.trim()
    if (!cleanUrl) throw new Error('Background audio URL cannot be empty.')
    await this.unlock()
    if (!this.context || !this.gain) throw new Error('Web Audio could not be initialized.')

    const version = ++this.loadVersion
    const response = await fetch(cleanUrl)
    if (!response.ok) throw new Error(`Could not load background audio (${response.status}).`)
    const buffer = await this.context.decodeAudioData(await response.arrayBuffer())
    if (version !== this.loadVersion) throw new Error('Background audio load was superseded.')

    this.stop(0)
    const source = this.context.createBufferSource()
    source.buffer = buffer
    source.loop = options.loop ?? true
    source.connect(this.gain)
    source.onended = () => {
      if (this.source === source) this.source = null
    }

    const volume = clamp01(options.volume ?? 0.55)
    const fadeIn = Math.max(0, options.fadeIn ?? 0.8)
    const now = this.context.currentTime
    this.gain.gain.cancelScheduledValues(now)
    this.gain.gain.setValueAtTime(fadeIn > 0 ? 0 : volume, now)
    if (fadeIn > 0) this.gain.gain.linearRampToValueAtTime(volume, now + fadeIn)
    this.source = source
    source.start()
    return source
  }

  public stop(fadeOut = 0.3): void {
    this.loadVersion += 1
    const source = this.source
    const context = this.context
    const gain = this.gain
    if (!source || !context || !gain) return
    this.source = null

    const duration = Math.max(0, fadeOut)
    const now = context.currentTime
    gain.gain.cancelScheduledValues(now)
    gain.gain.setValueAtTime(gain.gain.value, now)
    gain.gain.linearRampToValueAtTime(0, now + duration)
    source.stop(now + duration + 0.02)
  }

  public async playVoice(
    url: string,
    options: VoiceAudioOptions = {},
  ): Promise<AudioBufferSourceNode> {
    const cleanUrl = url.trim()
    if (!cleanUrl) throw new Error('Voice audio URL cannot be empty.')
    await this.unlock()
    if (!this.context || !this.voiceGain) throw new Error('Web Audio could not be initialized.')

    const version = ++this.voiceLoadVersion
    const response = await fetch(cleanUrl)
    if (!response.ok) throw new Error(`Could not load voice audio (${response.status}).`)
    const buffer = await this.context.decodeAudioData(await response.arrayBuffer())
    if (version !== this.voiceLoadVersion) throw new Error('Voice audio load was superseded.')

    this.stopVoice(0)
    const source = this.context.createBufferSource()
    source.buffer = buffer
    source.connect(this.voiceGain)
    source.onended = () => {
      if (this.voiceSource === source) this.voiceSource = null
    }

    const now = this.context.currentTime
    this.voiceGain.gain.cancelScheduledValues(now)
    this.voiceGain.gain.setValueAtTime(clamp01(options.volume ?? 1), now)
    this.voiceSource = source
    source.start()
    return source
  }

  public stopVoice(fadeOut = 0.12): void {
    this.voiceLoadVersion += 1
    const source = this.voiceSource
    const context = this.context
    const gain = this.voiceGain
    if (!source || !context || !gain) return
    this.voiceSource = null

    const duration = Math.max(0, fadeOut)
    const now = context.currentTime
    gain.gain.cancelScheduledValues(now)
    gain.gain.setValueAtTime(gain.gain.value, now)
    gain.gain.linearRampToValueAtTime(0, now + duration)
    source.stop(now + duration + 0.02)
  }

  public async playNotificationBeep(volume = 0.16): Promise<void> {
    await this.unlock()
    const context = this.context
    if (!context) throw new Error('Web Audio could not be initialized.')

    const level = Math.max(0.0001, clamp01(volume))
    const startAt = context.currentTime + 0.015
    const tones = [
      { delay: 0, from: 880, to: 1174.66, duration: 0.12 },
      { delay: 0.17, from: 1174.66, to: 1567.98, duration: 0.14 },
    ]

    tones.forEach((tone) => {
      const oscillator = context.createOscillator()
      const toneGain = context.createGain()
      const start = startAt + tone.delay
      const end = start + tone.duration

      oscillator.type = 'triangle'
      oscillator.frequency.setValueAtTime(tone.from, start)
      oscillator.frequency.exponentialRampToValueAtTime(tone.to, end)
      toneGain.gain.setValueAtTime(0.0001, start)
      toneGain.gain.exponentialRampToValueAtTime(level, start + 0.018)
      toneGain.gain.exponentialRampToValueAtTime(0.0001, end)
      oscillator.connect(toneGain)
      toneGain.connect(context.destination)
      oscillator.onended = () => {
        oscillator.disconnect()
        toneGain.disconnect()
      }
      oscillator.start(start)
      oscillator.stop(end + 0.015)
    })
  }

  /** Fetch before START so the user-gesture unlock only needs to decode 9 KB. */
  public async preparePhoneKeyTicks(url: string): Promise<void> {
    const cleanUrl = url.trim()
    if (!cleanUrl) throw new Error('Phone keyboard sound URL cannot be empty.')

    const version = ++this.phoneKeyTickLoadVersion
    const response = await fetch(cleanUrl)
    if (!response.ok) throw new Error(`Could not load phone keyboard sound (${response.status}).`)
    const data = await response.arrayBuffer()
    if (version !== this.phoneKeyTickLoadVersion) return

    this.phoneKeyTickData = data
    this.phoneKeyTickBuffer = null
    this.phoneKeyTickIndex = 0
    this.phoneKeyTickLastAt = Number.NEGATIVE_INFINITY
    await this.decodePreparedPhoneKeyTicks()
  }

  /**
   * Play one real phone-key recording. This method deliberately stays
   * synchronous so the sample is scheduled in the same task as a matching
   * visual key press instead of drifting behind the typewriter animation. A
   * short cadence gate coalesces dense bursts; the authored pauses remain.
   */
  public playPhoneKeyTick(
    _key: string,
    _kind: PhoneKeyTickKind,
    volume = PHONE_KEY_TICK_VOLUME,
  ): boolean {
    const context = this.context
    const buffer = this.phoneKeyTickBuffer
    if (!context || context.state !== 'running' || !buffer) return false

    const startAt = context.currentTime + 0.002
    if (startAt - this.phoneKeyTickLastAt < PHONE_KEY_TICK_MIN_GAP) return false
    this.phoneKeyTickLastAt = startAt

    const source = context.createBufferSource()
    const gain = context.createGain()
    const variant = this.phoneKeyTickIndex % PHONE_KEY_TICK_VARIANTS
    this.phoneKeyTickIndex += 1
    const offset = variant * PHONE_KEY_TICK_DURATION
    const duration = Math.min(PHONE_KEY_TICK_DURATION, buffer.duration - offset)

    source.buffer = buffer
    gain.gain.setValueAtTime(clamp01(volume), startAt)
    source.connect(gain)
    gain.connect(context.destination)
    source.onended = () => {
      source.disconnect()
      gain.disconnect()
    }
    source.start(startAt, offset, duration)
    return true
  }

  private async decodePreparedPhoneKeyTicks(): Promise<void> {
    const context = this.context
    const data = this.phoneKeyTickData
    if (!context || !data || this.phoneKeyTickBuffer) return

    const decoded = await context.decodeAudioData(data.slice(0))
    if (context === this.context) this.phoneKeyTickBuffer = decoded
  }

  /** Fetch once before START so every interaction can play in exact sync. */
  public async prepareWoosh(url: string): Promise<void> {
    const cleanUrl = url.trim()
    if (!cleanUrl) throw new Error('Woosh sound URL cannot be empty.')

    const version = ++this.wooshLoadVersion
    const response = await fetch(cleanUrl)
    if (!response.ok) throw new Error(`Could not load woosh sound (${response.status}).`)
    const data = await response.arrayBuffer()
    if (version !== this.wooshLoadVersion) return

    this.wooshData = data
    this.wooshBuffer = null
    await this.decodePreparedWoosh()
  }

  /** Reusable, overlapping-safe movement SFX; does not touch music or voice gain. */
  public playWoosh(volume = WOOSH_VOLUME): boolean {
    const context = this.context
    const buffer = this.wooshBuffer
    if (!context || context.state !== 'running' || !buffer) return false

    const source = context.createBufferSource()
    const gain = context.createGain()
    const startAt = context.currentTime + 0.002
    source.buffer = buffer
    gain.gain.setValueAtTime(clamp01(volume), startAt)
    source.connect(gain)
    gain.connect(context.destination)
    this.activeWooshSources.set(source, gain)
    source.onended = () => {
      this.activeWooshSources.delete(source)
      source.disconnect()
      gain.disconnect()
    }
    source.start(startAt)
    return true
  }

  private async decodePreparedWoosh(): Promise<void> {
    const context = this.context
    const data = this.wooshData
    if (!context || !data || this.wooshBuffer) return

    const decoded = await context.decodeAudioData(data.slice(0))
    if (context === this.context && data === this.wooshData) this.wooshBuffer = decoded
  }

  public async playDeliveryError(volume = 0.085): Promise<void> {
    await this.unlock()
    const context = this.context
    if (!context) throw new Error('Web Audio could not be initialized.')

    const level = Math.max(0.0001, clamp01(volume))
    const startAt = context.currentTime + 0.012
    const endAt = startAt + 0.32
    const oscillator = context.createOscillator()
    const toneGain = context.createGain()
    const filter = context.createBiquadFilter()

    oscillator.type = 'sine'
    oscillator.frequency.setValueAtTime(261.63, startAt)
    oscillator.frequency.exponentialRampToValueAtTime(185, endAt)
    filter.type = 'lowpass'
    filter.frequency.setValueAtTime(720, startAt)
    filter.Q.value = 0.4
    toneGain.gain.setValueAtTime(0.0001, startAt)
    toneGain.gain.exponentialRampToValueAtTime(level, startAt + 0.022)
    toneGain.gain.exponentialRampToValueAtTime(level * 0.42, startAt + 0.12)
    toneGain.gain.exponentialRampToValueAtTime(0.0001, endAt)
    oscillator.connect(filter)
    filter.connect(toneGain)
    toneGain.connect(context.destination)
    oscillator.onended = () => {
      oscillator.disconnect()
      filter.disconnect()
      toneGain.disconnect()
    }
    oscillator.start(startAt)
    oscillator.stop(endAt + 0.015)
  }

  public get isUnlocked(): boolean {
    return this.context?.state === 'running'
  }

  public get isPlaying(): boolean {
    return this.source !== null
  }

  public get isVoicePlaying(): boolean {
    return this.voiceSource !== null
  }

  public dispose(): void {
    this.stop(0)
    this.stopVoice(0)
    this.gain?.disconnect()
    this.gain = null
    this.voiceGain?.disconnect()
    this.voiceGain = null
    this.phoneKeyTickLoadVersion += 1
    this.phoneKeyTickData = null
    this.phoneKeyTickBuffer = null
    this.phoneKeyTickIndex = 0
    this.phoneKeyTickLastAt = Number.NEGATIVE_INFINITY
    this.wooshLoadVersion += 1
    this.wooshData = null
    this.wooshBuffer = null
    this.activeWooshSources.forEach((gain, source) => {
      source.onended = null
      try {
        source.stop()
      } catch {
        // It may have naturally ended between iteration and stop().
      }
      source.disconnect()
      gain.disconnect()
    })
    this.activeWooshSources.clear()
    const context = this.context
    this.context = null
    if (context && context.state !== 'closed') void context.close()
  }
}
